import React, { KeyboardEventHandler, useMemo } from 'react';
import { GridCellProps } from '@mui/x-data-grid';
import { DataGrid } from '../../../../libs/x-data-grid/DataGrid';
import { GridCell } from '../../../../libs/x-data-grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { SelectChangeEvent } from '@mui/material/Select';
import { TablePaginationProps } from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { ICloseEvent } from '@core/interfaces/ICloseEvent';
import { lang } from '@lang/interfaces/lang';
import AlertComponent from '@ui/components/Alert/Alert.component';
import FooterComponent from '@ui/features/Device-telemetry/Footer.component';
import useDictionary from '@ui/hooks/useDictionary';
import { IColumn, IErrorMap, IRow } from './useAddDeviceTable';

interface IProps {
    loading: boolean;
    lang: lang;
    page: number;
    limit: number;
    rows: IRow[];
    columns: IColumn[];
    uploadError: boolean;
    errorMap: IErrorMap;
    imeiNotExistError: boolean;
    wrongPasswordError: boolean;
    noRightsError: boolean;
    onPageChange: TablePaginationProps['onPageChange'];
    onLimitChange: (e: SelectChangeEvent<number>) => void;
    onCancelClick: () => void;
    onAddClick: () => void;
    onCellEditStart: () => void;
    onBtnKeyDown: KeyboardEventHandler<HTMLButtonElement>;
    onClose: ICloseEvent['onClose'];
}

const Component: React.FC<IProps> = ({
    loading,
    lang,
    page,
    limit,
    rows,
    columns,
    uploadError,
    errorMap,
    imeiNotExistError,
    wrongPasswordError,
    noRightsError,
    onPageChange,
    onLimitChange,
    onCancelClick,
    onAddClick,
    onCellEditStart,
    onBtnKeyDown,
    onClose,
}: IProps) => {
    const getWord = useDictionary();

    const mainAlertMessage = useMemo(() => {
        if (uploadError) return 'incorrectData';
        else if (imeiNotExistError && noRightsError) {
            return `
                ${getWord('imeiNotExistError')} 
                ${getWord('or').toLowerCase()} 
                ${getWord('noRightsError').toLowerCase()}
            `;
        } else if (imeiNotExistError) return 'imeiNotExistError';
        else if (noRightsError) return 'noRightsError';
        else if (wrongPasswordError) return 'wrongPasswordError';
        else return '';
    }, [
        lang,
        uploadError,
        imeiNotExistError,
        noRightsError,
        wrongPasswordError,
    ]);

    const isMainError = !!mainAlertMessage;

    const ErrorOverlay = () => {
        return (
            <Typography className="add-device-table__no-rows-overlay">
                {getWord('error')}
            </Typography>
        );
    };
    const NoRowsOverlay = () => {
        return (
            <Typography className="add-device-table__no-rows-overlay">
                {getWord('noData')}
            </Typography>
        );
    };
    const Footer = () => {
        const classes = {
            tooltipPopper: 'add-device-table__tooltip-popper',
            tooltip: 'add-device-table__tooltip',
            btnDownload: 'add-device-table__btn_download',
            ico: 'add-device-table__ico',
            btnVisibility: 'add-device-table__btn_visibility',
            btnBar: 'add-device-table__pagination-btn-bar',
            tablePagination: 'add-device-table__table-pagination',
            tablePaginationInput: 'add-device-table__table-pagination-input',
            tablePaginationInputItem:
                'add-device-table__table-pagination-input-item',
            tablePaginationInputTitle:
                'add-device-table__table-pagination-input-title',
            tablePaginationInputWrap:
                'add-device-table__table-pagination-input-wrap',
            tablePaginationActions:
                'add-device-table__table-pagination-actions',
            tablePaginationRowsLabel:
                'add-device-table__table-pagination-rows-label',
            tablePaginationSelectLabel:
                'add-device-table__table-pagination-select-label',
        };
        const props = {
            classes,
            lang,
            page,
            limit,
            loading: false,
            dataLength: rows.length,
            hideSettings: true,
            hideDownload: true,
            onPageChange,
            onLimitChange,
        };
        return (
            <>
                <FooterComponent {...props} />
            </>
        );
    };
    const Cell = (params: GridCellProps<string>) => {
        const props: GridCellProps<string> = { ...params };
        if (
            isMainError &&
            props.value &&
            errorMap[props.field as keyof typeof errorMap].includes(props.value)
        ) {
            props.className = 'add-device-table__table-cell_error';
        }
        return <GridCell {...props} />;
    };

    return (
        <Box className="add-device-table__wrap">
            <Box className="add-device-table" component="section">
                <IconButton
                    className="close-btn"
                    onClick={onClose}
                    onKeyDown={onBtnKeyDown}
                >
                    <CloseIcon className="close-btn__ico" />
                </IconButton>
                {!loading && (
                    <>
                        <AlertComponent
                            severity="error"
                            variant="modal"
                            lang={lang}
                            display={isMainError}
                            message={mainAlertMessage}
                        />
                        <Typography
                            className="add-device-table__title"
                            variant="h6"
                        >
                            {getWord('addDevicesTitle')}
                        </Typography>
                        <Box className="add-device-table__table-wrap">
                            <DataGrid
                                classes={{
                                    root: 'add-device-table__table',
                                    virtualScrollerRenderZone:
                                        'add-device-table__table-body',
                                    cell: 'add-device-table__table-cell',
                                    columnHeaderTitleContainer:
                                        'add-device-table__table-col-header',
                                    columnHeaderTitleContainerContent:
                                        'add-device-table__table-col-header-content',
                                    columnHeaderTitle:
                                        'add-device-table__table-col-header-title',
                                    virtualScroller:
                                        'add-device-table__table-virtual-scroller',
                                }}
                                columns={columns}
                                rows={rows}
                                page={page}
                                pageSize={limit}
                                headerHeight={28}
                                rowHeight={28}
                                rowsPerPageOptions={[]}
                                // loading={loading}
                                // page={page}
                                // columnVisibilityModel={columnVisibilityModel}
                                // sortModel={sortModel}
                                // onPageChange={onPageChange}
                                // onSortModelChange={onSortModelChange}
                                components={{
                                    ErrorOverlay,
                                    NoRowsOverlay,
                                    NoResultsOverlay: NoRowsOverlay,
                                    Footer,
                                    Cell,
                                }}
                                // error={serverError?true:null}
                                // disableSelectionOnClick
                                disableColumnMenu
                                onCellEditStart={onCellEditStart}
                            />
                        </Box>
                        <Box className="add-device-table__btn-bar">
                            <Button
                                className="add-device-table__btn"
                                variant="contained"
                                onClick={onAddClick}
                                onKeyDown={onBtnKeyDown}
                            >
                                {getWord('add')}
                            </Button>
                            <Button
                                className="add-device-table__btn"
                                variant="contained"
                                color="error"
                                onClick={onCancelClick}
                                onKeyDown={onBtnKeyDown}
                            >
                                {getWord('cancel')}
                            </Button>
                        </Box>
                    </>
                )}
                {loading && (
                    <Box className="add-device-table__loader">
                        <CircularProgress size={50} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const AddDeviceTableComponent = React.memo(Component);

export default AddDeviceTableComponent;
